import { useCallback, useRef, useSyncExternalStore } from 'react'

import {
  IconButton,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useClipboard,
} from '@chakra-ui/react'
import { Link as LinkIcon } from 'src/components/icon'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { openNewWindow } from 'src/utils/openNewWindow'

import { downloadObject } from '../../../../utils/downloadObject'
import { getFileType, isPreviewable } from '../../../misc/utils/fileUploadUtils'

export type HospitalSharedFile = {
  fileName: string
  fileSize: string
  fileExtension: string
  downloadUrl: string
  sharedLink: string
}

type Props = {
  sharedFiles: HospitalSharedFile[]
}

export const HospitalSharedFileTable: React.FC<Props> = ({
  sharedFiles,
}: Props) => {
  return (
    <Table variant="hoverable">
      <Thead position="sticky" top={0} zIndex={1} bg="white">
        <Tr>
          <Th p="2" w="auto">
            <Text as="h6">資料名</Text>
          </Th>
          <Th p="2" w="80px">
            <Text as="h6">容量</Text>
          </Th>
          <Th p="2" w="82px">
            <Text as="h6">共有リンク</Text>
          </Th>
        </Tr>
      </Thead>
      <Tbody
        sx={{
          '& tr': { cursor: 'auto' },
          '& tr:active': { backgroundColor: 'blue.50' },
        }}
      >
        {sharedFiles.map((data, i) => (
          <HospitalSharedFileTableRow key={`shared_files_${i}`} {...data} />
        ))}
      </Tbody>
    </Table>
  )
}

const HospitalSharedFileTableRow: React.FC<HospitalSharedFile> = props => {
  const toast = useMirohaToast()
  const { onCopy } = useClipboard(props.sharedLink)
  const tableCellRef = useRef<HTMLTableCellElement>(null)

  const handleCopy = () => {
    onCopy()
    toast({
      title: 'リンクをクリップボードにコピーしました。',
      status: 'success',
    })
  }

  const simulateTextWidth = (text: string, font: string) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return 0
    context.font = font
    return context.measureText(text).width
  }

  const truncateFileName = useCallback(
    (fileName: string) => {
      if (tableCellRef.current === null) return fileName
      const ellipsis = '...'
      const clientWidth = tableCellRef.current.clientWidth || 0
      const paddingLeft =
        Number(
          getComputedStyle(tableCellRef.current).paddingLeft.replace('px', ''),
        ) || 0
      const paddingRight =
        Number(
          getComputedStyle(tableCellRef.current).paddingRight.replace('px', ''),
        ) || 0
      const tableCellWidth = clientWidth - paddingLeft - paddingRight
      if (!tableCellWidth) return fileName

      /**
       * ファイル名を名前と拡張子に分ける
       * 分割した後の要素が1つしかない場合は名前とみなし、拡張子は空文字とする
       */
      const splitted = fileName.split('.')
      const extension = splitted.length > 1 ? splitted.pop() : ''
      const name = splitted.join('.')
      const fontSize = tableCellRef.current?.style.fontSize || '16px'
      const font = `${fontSize} ${getComputedStyle(tableCellRef.current).fontFamily}`

      if (simulateTextWidth(fileName, font) <= tableCellWidth) return fileName

      const maxFileNameWidth =
        tableCellWidth -
        simulateTextWidth(ellipsis, font) -
        (extension ? simulateTextWidth(`.${extension}`, font) : 0)

      let truncatedName = name
      while (simulateTextWidth(truncatedName, font) > maxFileNameWidth) {
        truncatedName = truncatedName.slice(0, -1)
      }

      return `${truncatedName}${ellipsis}${extension ? `.${extension}` : ''}`
    },
    [tableCellRef],
  )

  const subscribeContainerSize = useCallback((onStoreChange: () => void) => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(() => onStoreChange())
    })
    if (tableCellRef.current) {
      observer.observe(tableCellRef.current)
    }
    return () => observer.disconnect()
  }, [])

  const truncatedFileName = useSyncExternalStore(subscribeContainerSize, () => {
    return truncateFileName(props.fileName)
  })

  const fileType = getFileType(props.fileExtension)
  const previewable = isPreviewable(fileType)

  return (
    <Tr h="40px">
      <Td ref={tableCellRef} pl="2" pr="2" py="0" maxW="0" whiteSpace="nowrap">
        <Link
          color="blue.500"
          textDecoration="underline"
          href={props.sharedLink}
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault()
            if (previewable) {
              openNewWindow(props.sharedLink, 'newWindow')
            } else {
              downloadObject({ url: props.downloadUrl })
            }
          }}
        >
          {truncatedFileName}
        </Link>
      </Td>
      <Td pl="2" pr="2" py="0" w="80px">
        <Text whiteSpace="nowrap">{props.fileSize}</Text>
      </Td>
      <Td pl="2" pr="2" py="0" w="82px">
        <Tooltip label="リンクをコピー">
          <IconButton
            w="36px"
            h="36px"
            minW="auto"
            isRound
            variant="ghost"
            aria-label={props.sharedLink}
            icon={<LinkIcon />}
            onClick={handleCopy}
          />
        </Tooltip>
      </Td>
    </Tr>
  )
}
